/**
 * @fileoverview Module for `src/contexts/UserProfilePictureContext.js`.
 */

import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';

import logger from '../lib/logger';
import useLocalStorageState from '../lib/hooks/useLocalStorageState';

const defaultPictureUrl = 'https://i0.wp.com/cdn.auth0.com/avatars/vf.png';
const pictureUrlKey = 'picture_url';

const UserProfilePictureContext = createContext(null);

/**
 * Context provider for persisted user profile picture URL state.
 *
 * @param {Object} params
 * @returns {JSX.Element} Context provider wrapper.
 */
function UserProfilePictureProvider({ children }) {
  const [pictureUrl, setPictureUrl] = useLocalStorageState(pictureUrlKey, defaultPictureUrl);
  const value = useMemo(() => [pictureUrl, setPictureUrl], [pictureUrl, setPictureUrl]);

  return (
    <UserProfilePictureContext.Provider value={ value }>
      { children }
    </UserProfilePictureContext.Provider>
  );
}

UserProfilePictureProvider.propTypes = {
  children: PropTypes.element,
};

/**
 * Accessor hook for profile picture context.
 *
 * @returns {[string, Function]|null} Profile picture state tuple.
 */
function useUserProfilePicture() {
  const context = useContext(UserProfilePictureContext);
  if (context === null) {
    logger.error('useUserProfilePicture() must be called within a UserProfilePictureProvider');
  }
  return context;
}

export { UserProfilePictureProvider, useUserProfilePicture };
